// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 3: Foto y Datos de Boleta
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { candidatoService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onBack: () => void;
  onNext: () => void;
}

export default function Paso3FotoBoleta({ candidato, onSaved, onBack, onNext }: Props) {
  const nombrePorDefecto = candidato.apellido && candidato.nombre
  ? `${candidato.apellido}, ${candidato.nombre}${candidato.apodo ? ` "${candidato.apodo}"` : ''}`
  : '';
  const [form, setForm] = useState({
    apodo: candidato.apodo || '',
    nombreEnBoleta: candidato.nombreEnBoleta || nombrePorDefecto,
  });
  const [guardando, setGuardando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const montado = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const foto = candidato.documentos.find(d => d.tipo === 'FOTO_CANDIDATO');

  const guardarAhora = async (avisar = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setGuardando(true);
    try {
      await candidatoService.actualizar(candidato.id, form);
      onSaved();
      if (avisar) toast.success('Borrador guardado');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Autosave con debounce de 800ms
  useEffect(() => {
    if (!montado.current) {
      montado.current = true;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { guardarAhora(); }, 800);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const subirFoto = async (archivo: File) => {
    setSubiendoFoto(true);
    try {
      await candidatoService.subirDocumento(candidato.id, 'FOTO_CANDIDATO', archivo);
      toast.success('Foto subida');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo subir la foto');
    } finally {
      setSubiendoFoto(false);
    }
  };

  const eliminarFoto = async () => {
    if (!foto) return;
    try {
      await candidatoService.eliminarDocumento(foto.id);
      toast.success('Foto eliminada');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo eliminar la foto');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
        Estos datos van a aparecer en la Boleta Única de Sufragio y en los afiches de listas completas (Arts. 47 y 54 CE).
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Fotografía color del candidato</h3>
          <div className="flex gap-5 items-start">
            <div className="w-[140px] h-[180px] bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {foto ? (
                <img src={foto.urlDescarga} alt="Foto del candidato" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-500 text-center px-2">Vista previa<br />140 × 180 px</span>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <label className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-gray-500 text-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50">
                {subiendoFoto ? 'Subiendo...' : foto ? 'Reemplazar foto' : '+ Subir foto'}
                <input type="file" accept="image/png" className="hidden" disabled={subiendoFoto}
                  onChange={e => e.target.files?.[0] && subirFoto(e.target.files[0])} />
              </label>
              {foto && (
                <button type="button" onClick={eliminarFoto} className="text-xs text-red-500 hover:text-red-700">✕ Eliminar foto</button>
              )}
              <div className="p-2.5 bg-blue-50 rounded-lg text-xs text-blue-800">
                <strong>Requisitos técnicos:</strong>
                <ul className="mt-1.5 ml-4 list-disc space-y-0.5">
                  <li>PNG</li>
                  <li>Máximo 5 MB</li>
                  <li>Mínimo 400×600 px</li>
                  <li>Fondo blanco</li>
                  <li>Rostro en primer plano, encuadre desde la altura de los hombros</li>
                  <li>Reciente (últimos 12 meses)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">Datos para la boleta</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apodo o nombre de uso (opcional)</label>
            <input type="text" value={form.apodo} placeholder="Ejemplo: Anita"
              onChange={e => setForm(f => ({ ...f, apodo: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre tal como debe imprimirse en la boleta</label>
            <input type="text" value={form.nombreEnBoleta}
              onChange={e => setForm(f => ({ ...f, nombreEnBoleta: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cargo tal como debe imprimirse</label>
            <input type="text" value={candidato.cargo} readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-500" />
          </div>

          <div className="mt-2 p-4 bg-gray-50 rounded-lg">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2">Vista previa en boleta</div>
            <div className="bg-white border border-gray-300 rounded-lg p-3 flex items-center gap-3">
              <div className="w-12 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded overflow-hidden flex-shrink-0">
                {foto && <img src={foto.urlDescarga} alt="" className="w-full h-full object-cover" />}
              </div>
              <div>
                <div className="font-semibold text-sm">{form.nombreEnBoleta || '—'}</div>
                <div className="text-xs text-gray-500">{candidato.cargo}</div>
                <div className="text-[11px] text-blue-700 mt-0.5">{candidato.partido?.denominacion}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-gray-500 italic">Vista previa esquemática. El diseño definitivo se aprueba en audiencia pública posterior a la oficialización.</div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm">Elementos identificatorios de la agrupación</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sigla</label>
            <div className="text-gray-700">{candidato.partido?.sigla || '—'}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Número de lista</label>
            <div className="text-gray-500 italic">Pendiente — sorteo público del orden</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Emblema</label>
            <div className="text-gray-500 italic">Registrado en el partido</div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Se toman del registro de la agrupación y del sorteo público posterior a la oficialización. No son editables desde acá.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">← Volver</button>
        <button onClick={() => guardarAhora(true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
          {guardando ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button
          onClick={async () => { await guardarAhora(); onNext(); }}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
        >
          Continuar a documentación →
        </button>
      </div>
    </div>
  );
}
